// SAFE EXTENSION: Added custom_name & custom_description (no breaking changes)

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

import {
  getProformaInvoice,
  createProformaInvoice,
  updateProformaInvoice,
  getParties,
  getItems,
  createPartySnapshot,
  createItemSnapshot,
  calculateGST
} from "../services/firestore";

import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";

import { PartySelector } from "../components/PartySelector";
import { ItemSelector } from "../components/ItemSelector";
import { LineItemRow } from "../components/LineItemRow";

import {
  ArrowLeft,
  Save,
  Loader2,
  FileText,
  Plus,
  Building2
} from "lucide-react";

import { toast } from "sonner";

export const ProformaInvoiceFormPage = () => {

  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();

  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);

  const [selectedParty, setSelectedParty] = useState(null);
  const [lineItems, setLineItems] = useState([]);

  const [printFormat, setPrintFormat] = useState("simple");

  const [totals, setTotals] = useState({
    subtotal: 0,
    tax_total: 0,
    grand_total: 0
  });

  const [status, setStatus] = useState("draft");

  const [partyModalOpen, setPartyModalOpen] = useState(false);
  const [itemModalOpen, setItemModalOpen] = useState(false);

const defaultTerms = {
  prices: "Net, EX-Our Nagaon (Shiroli - P) Godown, inclusive of final discounts",
  gst: "Extra at actuals",
  insurance: "To be arranged by you",
  freight: "Tempo charges extra at actuals",
  packing: "Nil in standard conditions",
  inspection: "At our Shiroli Facility, Kolhapur",
  delivery: "At earliest",
  payments: "100% Advance along with Techno commercially Signed and Stamped PO",
  jurisdiction: "Kolhapur Courts jurisdiction only",
  validity: "15 days subject to manufacturer's price revision"
};

const [terms, setTerms] = useState(defaultTerms);

  /* =============================
     NORMALIZE ITEMS
  ==============================*/

  const normalizeItems = (items) => {
    return (items || []).map(i => ({
      ...i,
      custom_name:
        i.custom_name ||
        i.item_snapshot?.item_name ||
        i.item_snapshot?.name ||
        "",
      custom_description:
        i.custom_description ||
        i.item_snapshot?.description ||
        ""
    }));
  };

  /* =============================
     FETCH DATA
  ==============================*/

  useEffect(() => {
    fetchInitialData();
  }, [id, userData]);

  const fetchInitialData = async () => {

    if (!userData) return;

    setLoading(true);

    try {

      const [partiesData, itemsData] = await Promise.all([
        getParties(),
        getItems()
      ]);

      setParties(partiesData);
      setItems(itemsData);

      if (isEdit) {

        const pi = await getProformaInvoice(id);

        if (!pi) {
          toast.error("Proforma Invoice not found");
          navigate("/proforma-invoices");
          return;
        }

        const party = partiesData.find(
          p => p.id === pi.party_snapshot?.party_id
        );

        setSelectedParty(party || pi.party_snapshot);

        setLineItems(normalizeItems(pi.items || []));
        setTotals(pi.totals || totals);
        setStatus(pi.status || "draft");

        if (pi.print_format) setPrintFormat(pi.print_format);
        if (pi.terms) {
        setTerms({ ...defaultTerms, ...(pi.terms || {}) });
  }
      }

    } catch (error) {
      console.error(error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  /* =============================
     TOTALS (UNCHANGED)
  ==============================*/

  const calculateTotals = useCallback((items, partyGST) => {

    let subtotal = 0;
    let tax_total = 0;

    const updatedItems = items.map(item => {

      const qty = parseFloat(item.qty) || 0;
      const rate = parseFloat(item.rate) || 0;
      const discount = parseFloat(item.discount_percent) || 0;

      const gstPercent =
        item.item_snapshot?.GST_percent ??
        item.item_snapshot?.gst_percent ??
        0;

      const gross = qty * rate;
      const discountAmt = (gross * discount) / 100;
      const taxable = gross - discountAmt;

      const taxBreakup = calculateGST(partyGST, taxable, gstPercent);

      const taxAmount =
        taxBreakup.CGST +
        taxBreakup.SGST +
        taxBreakup.IGST;

      subtotal += taxable;
      tax_total += taxAmount;

      return {
        ...item,
        taxable_amount: taxable,
        tax_breakup: taxBreakup,
        total: taxable + taxAmount
      };

    });

    return {
      items: updatedItems,
      totals: {
        subtotal,
        tax_total,
        grand_total: subtotal + tax_total
      }
    };

  }, []);

  const recalcTotals = (items, party) => {

    const partyGST = party?.gst_number || party?.gst || "";

    const { items: updatedItems, totals: newTotals } =
      calculateTotals(items, partyGST);

    setLineItems(updatedItems);
    setTotals(newTotals);
  };

  /* =============================
     HANDLERS
  ==============================*/

  const handlePartySelect = (party) => {

    setSelectedParty(party);
    setPartyModalOpen(false);

    if (lineItems.length > 0) {
      recalcTotals(lineItems, party);
    }
  };

  const handleItemSelect = (item) => {

    const snapshot = createItemSnapshot(item);

    const newItem = {
      item_snapshot: snapshot,

      // ✅ NEW (SAFE ADDITION)
      custom_name: snapshot.item_name || "",
      custom_description: snapshot.description || "",

      qty: 1,
      rate: snapshot.rate || 0,
      discount_percent: 0,
      taxable_amount: 0,
      tax_breakup: { CGST: 0, SGST: 0, IGST: 0 },
      total: 0
    };

    recalcTotals([...lineItems, newItem], selectedParty);
    setItemModalOpen(false);
  };

  const handleLineItemChange = (index, field, value) => {

  const updated = [...lineItems];
  updated[index] = { ...updated[index], [field]: value };

  setLineItems(updated);

  if (["qty", "rate", "discount_percent"].includes(field)) {
    recalcTotals(updated, selectedParty);
  }
};

const handleRemoveLineItem = (index) => {

  const updated = lineItems.filter((_, i) => i !== index);

  recalcTotals(updated, selectedParty);
};

  /* =============================
     SUBMIT
  ==============================*/

  const handleSubmit = async (e) => {

    e.preventDefault();

    if (!selectedParty) {
      toast.error("Please select a party");
      return;
    }

    if (lineItems.length === 0) {
      toast.error("Please add items");
      return;
    }

    setSaving(true);

    try {

      const piData = {
        party_snapshot: createPartySnapshot(selectedParty),
        items: lineItems,
        totals,
        status,
        terms,
        print_format: printFormat
      };

      if (isEdit) {
        await updateProformaInvoice(id, piData);
        toast.success("Proforma Invoice updated");
      } else {
        const result = await createProformaInvoice(piData);
        toast.success(`Proforma Invoice ${result.pi_no} created`);
      }

      navigate("/proforma-invoices");

    } catch (error) {
      console.error(error);
      toast.error("Failed to save Proforma Invoice");
    } finally {
      setSaving(false);
    }
  };

  /* =============================
     UI
  ==============================*/

  if (loading) {
    return (
      <div className="p-6">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-96 mt-4" />
      </div>
    );
  }

  return (

    <div className="p-6 max-w-4xl mx-auto pb-32">

      <div className="flex items-center gap-4 mb-6">

        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/proforma-invoices")}
        >
          <ArrowLeft />
        </Button>

        <h1 className="text-2xl font-bold">
          {isEdit ? "Edit Proforma Invoice" : "New Proforma Invoice"}
        </h1>

      </div>

      <form onSubmit={handleSubmit} className="space-y-6">

        <Card>
          <CardHeader><CardTitle>Status</CardTitle></CardHeader>
          <CardContent>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="border rounded p-2"
            >
              <option value="draft">Draft</option>
              <option value="submitted">Submitted</option>
              <option value="payment_received">Payment Received</option>
              <option value="lost">Lost</option>
              <option value="in_process">In Process</option>
            </select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex gap-2"><Building2 /> Party</CardTitle>
          </CardHeader>

          <CardContent>
            {selectedParty ? (
              <div className="p-4 bg-slate-50 rounded flex justify-between">
                <div>
                  <h3 className="font-semibold">{selectedParty.name}</h3>
                  <p className="text-sm text-gray-500">{selectedParty.address}</p>
                </div>

                <Button variant="outline" onClick={() => setPartyModalOpen(true)}>
                  Change
                </Button>
              </div>
            ) : (
              <Button variant="outline" onClick={() => setPartyModalOpen(true)}>
                Select Party
              </Button>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex justify-between">
              <CardTitle className="flex gap-2"><FileText /> Items</CardTitle>
              <Button type="button" variant="outline" onClick={() => setItemModalOpen(true)}>
                <Plus /> Add Item
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {lineItems.map((item, index) => (
              <LineItemRow
                key={index}
                item={item}
                index={index}
                onChange={handleLineItemChange}
                onRemove={handleRemoveLineItem}
              />
            ))}
          </CardContent>
        </Card>

<Card>
  <CardHeader>
    <CardTitle>Terms & Conditions</CardTitle>
  </CardHeader>

  <CardContent className="space-y-4">
    {Object.keys(terms).map((key) => (
      <div key={key}>
        <label className="capitalize text-sm">{key}</label>
        <input
          className="border rounded p-2 w-full"
          value={terms[key]}
          onChange={(e) =>
            setTerms({
              ...terms,
              [key]: e.target.value
            })
          }
        />
      </div>
    ))}
  </CardContent>
</Card>

        <Button type="submit" disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" />
              Save
            </>
          )}
        </Button>

      </form>

      <PartySelector
        open={partyModalOpen}
        onOpenChange={setPartyModalOpen}
        parties={parties}
        onSelect={handlePartySelect}
        onCreateNew={() => navigate("/parties/new")}
      />

      <ItemSelector
        open={itemModalOpen}
        onOpenChange={setItemModalOpen}
        items={items}
        onSelect={handleItemSelect}
        onCreateNew={() => navigate("/items/new")}
      />

    </div>
  );
};

export default ProformaInvoiceFormPage;